"use client";

import { useCallback, useEffect, useState, type MouseEvent } from "react";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import SearchBar from "./SearchBar";
import ImageWrapper from "./ui/ImageWrapper";
import LoginSignup from "./LoginSignup";
import { getLocationString } from "@/app/utilities/locationCookie";
import { TRACKING } from "@/lib/constants/tracking";
import { acquireModalSession, releaseModalSession, subscribeModalSession } from "@/lib/client/modalSession";
import SearchHeader from "./SearchHeader";
import { SearchDropdownData } from '@/app/utilities/types/searchDropdown';
import { DIWALI_HEADER, DiwaliHeaderDecor, HEADER_LOGO_FILE } from "./DiwaliTheme";

const searchDropdownData: SearchDropdownData = {
    icons: {
        arrow: 'trending-icon.svg',
        car: 'car.svg',
        price: 'rupee-icon.svg',
        emi: 'emi-icon.svg',
        compare: 'swap-icon.svg',
        web: 'web-story-icon.svg',
        video: 'video-icon.svg',
        news: 'web-story-icon.svg',
        rightarrow: 'arrow-up-side.svg',
        exclamatory: 'exclamatory-white.svg',
    },
    sponsoredData: [],
    trendingData: [],
    sectionRanking: [],
    step2Data: {
        models: [],
        utility: [],
        listings: [],
        comparisons: [],
        editorial: [],
    },
};

const LocationSelectModal = dynamic(() => import("./LocationSelectModal"));

const LOCATION_MODAL_SESSION_ID = "header-location-modal";
const LOGIN_MODAL_SESSION_ID = "header-login-modal";
const GET_LOAN_HREF = "https://finj.in/?utm_source=cj&utm_medium=header";
const GET_LOAN_SRC = `${process.env.NEXT_PUBLIC_CDN_URL}get-loan.webp?width=100&quality=80&format=webp`;

function GetLoanBadge({ className = "" }: { className?: string }) {
    return (
        <Link
            href={GET_LOAN_HREF}
            target="_blank"
            rel="noreferrer nofollow"
            className={`flex-shrink-0 ${className}`}
            aria-label="Get Loan"
        >
            <img
                src={GET_LOAN_SRC}
                width={100}
                height={36}
                alt="Get Loan"
                title="Get Loan"
                decoding="async"
                className="h-9 w-[100px] object-contain md:h-[45px]"
            />
        </Link>
    );
}

export default function Header() {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
    const [openNestedMenu, setOpenNestedMenu] = useState<string | null>(null);
    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const [selectedCity, setSelectedCity] = useState<string | null>(null);
    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const pathname = usePathname();
    const router = useRouter();

    useEffect(() => {
        const locationString = getLocationString();
        if (locationString) {
            setSelectedCity(locationString);
        }
    }, []);

    const openLocationModal = useCallback(() => {
        acquireModalSession(LOCATION_MODAL_SESSION_ID);
        setIsLocationModalOpen(true);
    }, []);

    const closeLocationModal = useCallback(() => {
        setIsLocationModalOpen(false);
        releaseModalSession(LOCATION_MODAL_SESSION_ID);
    }, []);

    const openLoginModal = useCallback(() => {
        acquireModalSession(LOGIN_MODAL_SESSION_ID);
        setLoginModalOpen(true);
    }, []);

    const closeLoginModal = useCallback(() => {
        setLoginModalOpen(false);
        releaseModalSession(LOGIN_MODAL_SESSION_ID);
    }, []);

    const toggleMobileMenu = () => setIsMobileMenuOpen((s) => !s);

    const isActive = (path: string) => {
        if (!pathname) return false;
        if (path === "/") return pathname === "/";
        return pathname === path || pathname.startsWith(path + "/");
    };

    const [myCookie, setMyCookie] = useState<string | null>(null);
    const [myUser, setMyUser] = useState<any | null>(null);

    useEffect(() => {
        if (!loginModalOpen) {
            const allCookies = document.cookie; // e.g. "token=abc; theme=dark"
            const parts = allCookies.split('; ').map(c => c.split('='));
            const cookieMap: Record<string, string> = {};
            for (const p of parts) {
                if (!p || p.length === 0) continue;
                const key = p[0];
                const val = p.slice(1).join('=');
                cookieMap[key] = val;
            }
            setMyCookie(cookieMap['token'] || null);
            try {
                setMyUser(cookieMap['user'] ? JSON.parse(decodeURIComponent(cookieMap['user'])).user : null);
            } catch {
                setMyUser(null);
            }
        }
    }, [loginModalOpen]);

    // Listen for location-updated event to update selected city
    // Also listen for `open-location-modal` to open the centralized modal
    useEffect(() => {
        const handleLocationUpdate = (event: Event) => {
            const customEvent = event as CustomEvent;
            const { displayString } = customEvent.detail || {};

            if (displayString) {
                setSelectedCity(displayString);
            } else {
                // fallback: read from cookie
                const locationString = getLocationString();
                setSelectedCity(locationString);
            }
            router.refresh();
        };

        const handleOpenModal = () => openLocationModal();

        window.addEventListener("location-updated", handleLocationUpdate);
        window.addEventListener("open-location-modal", handleOpenModal as EventListener);

        return () => {
            window.removeEventListener("location-updated", handleLocationUpdate);
            window.removeEventListener("open-location-modal", handleOpenModal as EventListener);
        };
    }, [openLocationModal]);

    useEffect(() => {
        const unsubscribe = subscribeModalSession((activeId) => {
            if (activeId !== LOCATION_MODAL_SESSION_ID) {
                setIsLocationModalOpen(false);
            }

            if (activeId !== LOGIN_MODAL_SESSION_ID) {
                setLoginModalOpen(false);
            }
        });

        return unsubscribe;
    }, []);

    useEffect(() => {
        return () => {
            releaseModalSession(LOCATION_MODAL_SESSION_ID);
            releaseModalSession(LOGIN_MODAL_SESSION_ID);
        };
    }, []);


    const navigationItems = [
        {
            name: "New Cars",
            path: "/new-cars",
            subMenu: [
                { name: "Explore New Cars", path: "/new-cars" },
                { name: "Latest Cars", path: "/latest-cars" },
                { name: "Upcoming Cars", path: "/upcoming-cars" },
                {
                    name: "Popular Brands",
                    path: "/brands",
                    subItems: [
                        { name: "Maruti Suzuki Cars", path: "/maruti" },
                        { name: "Hyundai Cars", path: "/hyundai" },
                        { name: "Tata Cars", path: "/tata" },
                        { name: "Mahindra Cars", path: "/mahindra" },
                        { name: "Toyota Cars", path: "/toyota" },
                        { name: "Kia Cars", path: "/kia" },
                        { name: "Honda Cars", path: "/honda" },
                        { name: "BMW Cars", path: "/bmw" },
                        { name: "MG Cars", path: "/mg" },
                        { name: "Skoda Cars", path: "/skoda" },
                        { name: "View All Brands", path: "/brands" },
                    ]
                },
                {
                    name: "Popular Cars",
                    path: "/popular-cars",
                    subItems: [
                        { name: "Mahindra Bolero", path: "/mahindra/bolero" },
                        { name: "Toyota Camry", path: "/toyota/camry" },
                        { name: "Mahindra Bolero Neo", path: "/mahindra/bolero-neo" },
                        { name: "Tata Nexon", path: "/tata/nexon" },
                        { name: "Maruti Suzuki Brezza", path: "/maruti/brezza" },
                        { name: "Mahindra Scorpio N", path: "/mahindra/scorpio-n" },
                        { name: "Tata Punch", path: "/tata/punch" },
                        { name: "Tata Sierra", path: "/tata/sierra" },
                        { name: "Kia Seltos", path: "/kia/seltos" },
                        { name: "Hyundai Creta", path: "/hyundai/creta" },
                        { name: "All Popular Cars", path: "/popular-cars" },
                    ]
                },
                {
                    name: "Cars by Budget",
                    path: "#",
                    subItems: [
                        { name: "Under 5L", path: "/new-cars/cars-under-5-lakh" },
                        { name: "Under 10L", path: "/new-cars/cars-under-10-lakh" },
                        { name: "Under 15L", path: "/new-cars/cars-under-15-lakh" },
                        { name: "Under 20L", path: "/new-cars/cars-under-20-lakh" },
                        { name: "Under 35L", path: "/new-cars/cars-under-35-lakh" },
                        { name: "Under 50L", path: "/new-cars/cars-under-50-lakh" },
                        { name: "Above 50L", path: "/new-cars/cars-above-50-lakh" },
                    ]
                },
                {
                    name: "Cars by Body Type",
                    path: "#",
                    subItems: [
                        { name: "SUV", path: "/new-cars/suv-cars" },
                        { name: "Sedan", path: "/new-cars/sedan-cars" },
                        { name: "Hatchback", path: "/new-cars/hatchback-cars" },
                        { name: "Convertible", path: "/new-cars/convertible-cars" },
                        { name: "Coupe", path: "/new-cars/coupe-cars" },
                        { name: "MUV", path: "/new-cars/muv-cars" },
                        { name: "MPV", path: "/new-cars/mpv-cars" },
                        { name: "Luxury", path: "/new-cars/luxury-cars" },
                        { name: "Minivan", path: "/new-cars/minivan-cars" },
                        { name: "Pickup", path: "/new-cars/pickup-cars" },
                    ]
                },
                {
                    name: "Cars by Fuel Type",
                    path: "#",
                    subItems: [
                        { name: "Petrol", path: "/petrol-cars" },
                        { name: "Diesel", path: "/diesel-cars" },
                        { name: "CNG", path: "/cng-cars" },
                        { name: "Electric", path: "/electric-cars" },
                        { name: "Hybrid", path: "/hybrid-cars" },
                    ]
                },
                {
                    name: "Cars by Seating Capacity",
                    path: "#",
                    subItems: [
                        { name: "2 Seater", path: "/new-cars/2-seater-cars" },
                        { name: "4 Seater", path: "/new-cars/4-seater-cars" },
                        { name: "5 Seater", path: "/new-cars/5-seater-cars" },
                        { name: "6 Seater", path: "/new-cars/6-seater-cars" },
                        { name: "7 Seater", path: "/new-cars/7-seater-cars" },
                        { name: "8 Seater", path: "/new-cars/8-seater-cars" },
                        { name: "9 Seater", path: "/new-cars/9-seater-cars" },
                    ]
                },

                {
                    name: "Car EMI Calculator",
                    path: "/car-loan-emi-calculator"
                },
                { name: "Find Car Dealers", path: "/dealer-showrooms" },
            ],
        },

        {
            name: "Electric Cars",
            path: "/electric-cars",
            // icon: <Tags size={18} />,
            subMenu: [
                { name: "Tata Sierra EV", path: "/tata/sierra-ev" },
                { name: "Tata Punch EV", path: "/tata/punch-ev" },
                { name: "Maruti e Vitara", path: "/maruti/e-vitara" },
                { name: "Mahindra BE 6", path: "/mahindra/be-6" },
                { name: "All Electric Cars", path: "/electric-cars" },
            ],
        },
        { name: "Compare Cars", path: "/compare-cars" },
        {
            name: "News & Reviews",
            path: "/news",
            subMenu: [
                { name: "Latest Car News", path: "/news" },
                { name: "Car Expert Reviews", path: "/car-expert-reviews" },
                { name: "Web Story", path: "/web-stories" }
            ]
        },
    ];

    function handleLogout(event: MouseEvent<HTMLButtonElement>): void {
        event.preventDefault();

        const deleteCookie = (name: string) => {
            // clear cookie for current host and root path
            document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax;`;
            try {
                // also attempt with domain (some setups store cookies with domain)
                document.cookie = `${name}=; Domain=${window.location.hostname}; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax;`;
            } catch {
                // ignore if window or hostname not available
            }
        };

        deleteCookie("token");
        deleteCookie("user");

        // update local state
        setMyCookie(null);
        setMyUser(null);

        // close any open menus/modals
        setIsMobileMenuOpen(false);
        closeLoginModal();

        // reload to ensure UI/server see logout (optional but useful)
        if (typeof window !== "undefined") {
            window.location.reload();
        }
    }

    return (
        <header className={`sticky top-0 z-100 bg-white border-b border-gray-200 py-3 md:py-4 ${DIWALI_HEADER.root}`}>
            <DiwaliHeaderDecor />
            {/* Location Select Modal */}
            <LocationSelectModal
                showModal={isLocationModalOpen}
                onClose={closeLocationModal}
                onSelectCity={(city) => {
                    setSelectedCity(city);
                    closeLocationModal();
                }}
            />
            <nav className="container mx-auto px-5 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-auto h-md-16">
                    {/* Logo */}
                    <div className="xl:hidden">
                        <button
                            onClick={toggleMobileMenu}
                            className={`text-charcoal hover:text-automotive-red focus:outline-none transition-colors duration-200 text-[28px] mr-[20px] ${DIWALI_HEADER.menuToggle}`}
                            aria-label="Toggle mobile menu"
                        >
                            {isMobileMenuOpen ? <Image
                                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/cross-icon-new.svg`}
                                alt="cross icon"
                                title="cross icon"
                                width={16}
                                height={16}
                                className="invert"
                            /> : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                                    <path d="M3 6h18M3 12h18M3 18h18" />
                                </svg>
                            )}
                        </button>
                    </div>
                    <Link href="/" className="flex items-center space-x-3" prefetch={false} title="Car Junction">
                        <Image
                            src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}${HEADER_LOGO_FILE}`}
                            width={96}
                            height={48}
                            alt="Car Junction"
                            title="Car Junction"
                        />
                    </Link>
                    <GetLoanBadge className="md:hidden ml-auto mr-2" />

                    <button
                        onClick={openLocationModal}
                        className="md:hidden mr-1 flex items-center p-1 text-charcoal"
                        title={selectedCity ? `Change city (${selectedCity})` : "Select city"}
                    >
                        <Image
                            src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}location_on.svg`}
                            alt="Location"
                            title="Location"
                            width={14}
                            height={19}
                            className={DIWALI_HEADER.icon}
                        />
                    </button>

                    {/* <SearchBar /> */}
                    <SearchHeader searchDropdownData={searchDropdownData} />


                    {/* Desktop Navigation */}
                    <div className="hidden xl:flex items-center space-x-4 lg:space-x-8">
                        {navigationItems.map((item) => (
                            <div key={item.path} className="relative group desktop-menu desktopMenuSpace">
                                {item.subMenu ? (
                                    <span
                                        className={`${TRACKING.COMPONENT.NAVIGATION_CLICK} inline-flex items-center gap-2 font-semibold text-[13px] md:text-[14px] transition-all duration-200 pt-4 pb-4 ${isActive(item.path)
                                            ? "underline-offset-8"
                                            : "text-[#374151] hover:underline underline-offset-8 capitalize"
                                            } ${DIWALI_HEADER.navLink}`}
                                    >
                                        {item.name}
                                    </span>
                                ) : (
                                    <Link
                                        href={item.path}
                                        className={`${TRACKING.COMPONENT.NAVIGATION_CLICK} inline-flex items-center gap-2 font-semibold text-[13px] md:text-[14px] transition-all duration-200 pt-4 pb-4 ${isActive(item.path)
                                            ? "underline-offset-8"
                                            : "text-[#374151] hover:underline underline-offset-8 capitalize"
                                            } ${DIWALI_HEADER.navLink}`}
                                        prefetch={false}
                                        title={item.name}
                                    >
                                        {item.name}
                                    </Link>
                                )}
                                {item.subMenu && <span className="ml-2 inline-block w-3 h-3.5">

                                    <Image
                                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}dropdown-arrow.svg`}
                                        alt="Dropdown Arrow icon" title="Dropdown Arrow" width={12} height={14}
                                        className={`w-full h-full mt-1 ${DIWALI_HEADER.icon}`}
                                    />
                                </span>}

                                {item.subMenu && (
                                    <div className={`absolute right-0 top-full mt-0 hidden group-hover:block bg-white border border-gray-200 shadow-md z-999 min-w-[220px] ${DIWALI_HEADER.dropdown}`}>
                                        <div className="divide-y divide-gray-100">
                                            {item.subMenu.map((sub: any) => (
                                                <div key={sub.name} className="relative group/sub">
                                                    {sub.subItems ? (
                                                        <span
                                                            className="flex justify-between items-center px-5 py-3 text-[14px] capitalize font-semibold tracking-wide text-gray-700 hover:bg-gray-50"
                                                        >
                                                            {sub.name}
                                                            {/* <ImageWrapper
                                                                src={'/dropdown-arrow.svg'}
                                                                alt="Right Arrow"
                                                                title="Right Arrow"
                                                                width={10}
                                                                height={10}
                                                                className="-rotate-90 ml-2"
                                                                cdnBaseUrl={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}`}
                                                            /> */}
                                                            <Image
                                                                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}dropdown-arrow.svg`}
                                                                alt="Right Arrow"
                                                                title="Right Arrow"
                                                                width={10}
                                                                height={10}
                                                                className="-rotate-90 ml-2"

                                                            />
                                                        </span>
                                                    ) : (
                                                        <Link
                                                            href={sub.path}
                                                            className="flex justify-between items-center px-5 py-3 text-[14px] capitalize font-semibold tracking-wide text-gray-700 hover:bg-gray-50"
                                                            prefetch={false}
                                                            title={sub.name}
                                                        >
                                                            {sub.name}
                                                        </Link>
                                                    )}
                                                    {sub.subItems && (
                                                        <div className={`absolute left-full top-[-138px] hidden group-hover/sub:block bg-white border border-gray-200 shadow-md z-999 min-w-[220px] ${DIWALI_HEADER.dropdown}`}>
                                                            <div className="divide-y divide-gray-100">
                                                                {sub.subItems.map((child: any) => (
                                                                    <Link
                                                                        key={child.name}
                                                                        href={child.path}
                                                                        className="block px-6 py-3 text-[14px] capitalize font-semibold tracking-wide text-gray-700 hover:bg-gray-50"
                                                                        prefetch={false}
                                                                        title={child.name}
                                                                    >
                                                                        {child.name}
                                                                    </Link>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {/* {!myCookie ? (
              <div>
                <span
                  onClick={() => { setLoginModalOpen(true); }}
                  className="font-bold text-[14px] capitalize text-charcoal hover:underline underline-offset-8 cursor-pointer pt-4 pb-4"
                  style={{ userSelect: 'none' }}
                >
                  Login/Sign up
                </span>
              </div>
            ) : (
              myUser &&
              <span className="font-bold text-[14px] text-charcoal pt-4 pb-4">
                {myUser.name} 
              </span>
            )} */}
                    </div>

                    <GetLoanBadge className="hidden md:inline-block ml-auto xl:ml-3" />

                    {/* Tablet / Small Desktop – Icon Only */}
                    <button
                        onClick={openLocationModal}
                        className={`hidden md:flex xl:hidden ml-2 h-10 w-10 items-center justify-center rounded-full hover:bg-gray-100 transition ${DIWALI_HEADER.iconButton}`}
                    >
                        <Image
                            src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}location_on.svg`}
                            alt="Location Icon"
                            title="Location"
                            width={16}
                            height={20}
                            className={DIWALI_HEADER.icon}
                        />
                    </button>

                    {/* Desktop (XL) – Icon + City */}
                    <div className="hidden xl:flex items-center ml-4">
                        <button
                            onClick={openLocationModal}
                            title={selectedCity ? `Change city (${selectedCity})` : "Select city"}
                            className={`flex items-center gap-2 px-2 h-10 rounded-lg bg-[#F9F9F9] text-charcoal text-[13px] md:text-[12px] font-semibold hover:bg-gray-200 transition cursor-pointer ${DIWALI_HEADER.cityButton}`}
                        >

                            <Image
                                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}location_on.svg`}
                                alt="Location Icon"
                                title="Location"
                                width={13}
                                height={16}
                                className={DIWALI_HEADER.icon}
                            />
                            <span className="text-[12px] max-w-[98px] truncate">
                                {selectedCity || "Select City"}
                            </span>


                            <Image
                                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}drop-arrow.svg`}
                                alt="Dropdown Arrow"
                                width={10}
                                height={10}
                                className={DIWALI_HEADER.icon}
                                title="Dropdown Arrow"
                            />
                        </button>
                    </div>


                    {/* Account */}

                    {/* <div className="relative group hidden xl:inline-block">
                        {!myCookie ? (
                            <button
                                className="flex items-center p-2 pb-3 bg-[#F9F9F9] rounded-lg text-charcoal font-semibold text-sm hover:bg-gray-200 transition ml-3 gap-2"
                                onClick={() => {
                                    setIsMobileMenuOpen(false);
                                    openLoginModal();
                                }}
                            >
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}user-icon.svg`}
                                    alt="User Icon"
                                    title="User Icon"
                                    width={36}
                                    height={36}
                                    className="text-charcoal"
                                />

                                <div className="flex flex-col text-left">
                                    <span className="text-[12px] text-gray-600 truncate w-[60px]">Sign in</span>
                                    <div className="flex items-center">
                                        <span className="font-medium text-[13px] leading-[20px]">Account</span>
                                        <Image
                                            src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}drop-arrow.svg`}
                                            alt="cross icon"
                                            width={10}
                                            height={10}
                                            className="ml-2 transition-transform duration-200 group-hover:rotate-180"
                                            title="Dropdown Arrow"
                                        />
                                    </div>
                                </div>
                            </button>
                        ) : (
                            myUser && (
                                <div className="group inline-block relative ml-3">
                                    <button className="flex items-center p-2 pb-3 bg-[#F9F9F9] rounded-lg text-charcoal font-semibold text-sm hover:bg-gray-200 transition">
                                        <Image
                                            src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}user-icon.svg`}
                                            alt="User Icon"
                                            title="User Icon"
                                            width={36}
                                            height={36}
                                            className="text-charcoal"
                                        />
                                        <div className="flex flex-col text-left">
                                            <span className="text-sm text-gray-600 truncate w-[60px]">Welcome</span>
                                            <div className="flex items-center">
                                                <span className="font-medium text-[14px] leading-[20px] truncate w-[80px]">
                                                    {myUser.name}
                                                </span>
                                                <Image
                                                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}drop-arrow.svg`}
                                                    alt="Dropdown Arrow"
                                                    width={10}
                                                    height={10}
                                                    className="ml-2 transition-transform duration-200 group-hover:rotate-180"
                                                    title="Dropdown Arrow"
                                                />
                                            </div>
                                        </div>
                                    </button>
                                    <div className="absolute left-0 w-40 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 z-50">
                                        <ul className="py-2">
                                            <li>
                                                <button
                                                    onClick={handleLogout}
                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                >
                                                    Sign Out
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            )
                        )}
                    </div> */}

                </div>
            </nav>

            {/* Overlay */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/75 z-40"
                    onClick={() => setIsMobileMenuOpen(false)}
                ></div>
            )}

            {/* Mobile Navigation Drawer */}
            <div
                className={`fixed top-0 left-0 w-64 bg-white border-r border-gray-200 z-50 transform transition-transform duration-300 ease-in-out  h-[100dvh] md:h-screen overflow-y-auto scroll-smooth
        ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"} ${DIWALI_HEADER.drawer}`}
            >
                {/* <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <button className="flex items-center space-x-2 hover:text-automotive-red transition" onClick={() => { setIsMobileMenuOpen(false); setLoginModalOpen(true); }}>
            
            <ImageWrapper src={'/user-icon.svg'} alt="Search" width={36} height={36} cdnBaseUrl={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}`} className="text-charcoal w-[36px] h-[36px] rounded-full object-cover"/>

            
            <div className="flex flex-col text-left">
              <span className="text-sm text-gray-600 truncate w-[60px]">Sign in</span>
              <div className="flex items-center">
                <span className="font-semibold text-[14px] leading-[20px]">Account</span>
                <Image
                  src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}drop-arrow.svg`}
                  width={10}
                  height={10}
                  alt="Dropdown Icon"
                  className="ml-2 transition-transform duration-200 group-hover:rotate-180"
                />
              </div>
            </div>
          </button>
        </div> */}

                <div className="flex justify-between items-center p-4 border-b border-gray-200">
                    {!myCookie ? (
                        <button
                            className="flex items-center space-x-2 hover:text-automotive-red transition"
                            onClick={() => {
                                setIsMobileMenuOpen(false);
                                openLoginModal();
                            }}
                        >
                            <Image
                                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}user-icon.svg`}
                                alt="User Icon"
                                title="User Icon"
                                width={36}
                                height={36}
                                className="text-charcoal w-[36px] h-[36px] rounded-full object-cover"
                            />

                            <div className="flex flex-col text-left">
                                <span className="text-sm text-gray-600 truncate w-[60px]">Sign in</span>
                                <div className="flex items-center">
                                    <span className="font-semibold text-[14px] leading-[20px]">Account</span>
                                    <Image
                                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}drop-arrow.svg`}
                                        width={8}
                                        height={8}
                                        alt="Dropdown Icon"
                                        title="Dropdown Icon"
                                        className="ml-2 transition-transform duration-200 group-hover:rotate-180"
                                    />
                                </div>
                            </div>
                        </button>
                    ) : (
                        <button
                            className="flex items-center space-x-2 hover:text-automotive-red transition"
                            onClick={handleLogout}
                        >
                            <Image
                                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}user-icon.svg`}
                                alt="User Icon"
                                title="User Icon"
                                width={36}
                                height={36}
                                className="text-charcoal w-[36px] h-[36px] rounded-full object-cover"
                            />

                            <div className="flex flex-col text-left">
                                <span className="text-sm text-gray-600 truncate w-[60px]">
                                    {myUser?.name || 'User'}
                                </span>
                                <div className="flex items-center">
                                    <span className="font-semibold text-[14px] leading-[20px]">Logout</span>
                                    <Image
                                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}drop-arrow.svg`}
                                        width={8}
                                        height={8}
                                        alt="Dropdown Icon"
                                        title="Dropdown Icon"
                                        className="ml-2 transition-transform duration-200 group-hover:rotate-180"
                                    />
                                </div>
                            </div>
                        </button>
                    )}
                </div>


                <div className="px-2 pt-2 pb-3 space-y-1">
                    {navigationItems.map((item) => (
                        <div key={item.path}>
                            <div
                                className={`flex items-center justify-between px-3 py-2 rounded-md transition-colors duration-200 ${isActive(item.path)
                                    ? "text-automotive-red font-semibold"
                                    : "text-charcoal hover:text-automotive-red hover:bg-gray-50"
                                    }`}
                            >
                                {item.subMenu ? (
                                    <button
                                        onClick={() => {
                                            setOpenSubMenu(
                                                openSubMenu === item.name ? null : item.name
                                            );
                                            setOpenNestedMenu(null);
                                        }}
                                        className="flex items-center flex-1 text-left gap-2 text-[13px] capitalize font-semibold"
                                    >
                                        {item.name}
                                    </button>
                                ) : (
                                    <Link
                                        href={item.path}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="flex items-center font-semibold flex-1 gap-2 capitalize text-[13px]"
                                        prefetch={false}
                                    >
                                        {item.name}
                                    </Link>
                                )}
                                {item.subMenu && (
                                    <Image
                                        src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}dropdown-arrow.svg`}
                                        alt="Dropdown Arrow"
                                        title="Dropdown Arrow"
                                        width={12}
                                        height={12}
                                        className={`ml-2 cursor-pointer transition-transform duration-300 ${openSubMenu === item.name ? "rotate-180" : "rotate-0"}`}
                                        onClick={() => {
                                            setOpenSubMenu(openSubMenu === item.name ? null : item.name);
                                            setOpenNestedMenu(null);
                                        }}
                                    />
                                )}
                            </div>
                            {item.subMenu && openSubMenu === item.name && (
                                <div className="bg-white border-t border-gray-100">
                                    {item.subMenu.map((sub: any) => (
                                        <div key={sub.name}>
                                            {sub.subItems ? (
                                                <>
                                                    <button
                                                        onClick={() => setOpenNestedMenu(openNestedMenu === sub.name ? null : sub.name)}
                                                        className="flex items-center justify-between w-full px-5 py-3 text-[13px] capitalize font-semibold text-gray-700 hover:bg-gray-50"
                                                    >
                                                        {sub.name}
                                                        <Image
                                                            src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}dropdown-arrow.svg`}
                                                            alt="Dropdown Arrow"
                                                            title="Dropdown Arrow"
                                                            width={12}
                                                            height={12}
                                                            className={`ml-2 cursor-pointer transition-transform duration-300 ${openNestedMenu === sub.name ? "rotate-180" : "rotate-0"}`}
                                                        />
                                                    </button>
                                                    {openNestedMenu === sub.name && (
                                                        <div className="bg-gray-50 border-y border-gray-100">
                                                            {sub.subItems.map((child: any) => (
                                                                <Link
                                                                    key={child.name}
                                                                    href={child.path}
                                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                                    className="block px-10 py-3 text-[12px] capitalize font-semibold text-gray-600 hover:bg-gray-100"
                                                                    prefetch={false}
                                                                >
                                                                    {child.name}
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <Link
                                                    href={sub.path}
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                    className="block px-6 py-3 text-[13px] capitalize font-semibold text-gray-700 hover:bg-gray-50"
                                                    prefetch={false}
                                                >
                                                    {sub.name}
                                                </Link>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}

                    {/* <div className="absolute bottom-0 left-0 w-full px-[28px] py-3">
            <button
              className="flex items-center gap-2 text-[14px] font-semibold text-[#FF0000] transition p-3"
            >
              <ImageWrapper src={`/sign-out.svg`} alt="Sign Out" width={20} height={20} cdnBaseUrl={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}`}/>
              Sign Out
            </button>
          </div> */}
                </div>
            </div>

            {/* Login/Sign up Modal */}
            {loginModalOpen && (
                <LoginSignup open={loginModalOpen} onClose={closeLoginModal} />
            )}
        </header>
    );
}